import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import TableRestaurantIcon from "@mui/icons-material/TableRestaurant";
import GroupsIcon from "@mui/icons-material/Groups";
import EditIcon from "@mui/icons-material/Edit";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import { DigipingMark } from "../../digiping";
import { useBrand } from "../../context/BrandContext";
import { isDemoClub } from "../../utils/demoClub";
import { DemoGuestInviteQrOverlay, DemoGuestInviteQrToggleButton } from "../../components/DemoPhoneJoinLink";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import {
  resolveCoachTableSideRoles,
  resolvePlayerDisplayFields,
} from "../../utils/liveTrainingPlayerContext";
import {
  kotTableDisplayRows,
  pickMatchForKotStopRequest,
} from "../../utils/kotCoachRound";
import {
  canCoachShowResultButton,
  coachMustStopRoundBeforeResult,
  isRealKotMatchId,
} from "../../utils/kotCoachSubmit";
import { useKotCoachSubmitResult } from "../../hooks/useKotCoachSubmitResult";
import { useKotCoachRound } from "../../hooks/useKotCoachRound";
import { useCoachSessionTables } from "../../hooks/useCoachSessionTables";
import { emitCoachJoinTrainingSession } from "../../coaching/coachExerciseLiveActions";
import { KotCoachResultDialog } from "../../components/KotCoachResultDialog";
import PlayerPresenceDialog from "../../components/PlayerPresenceDialog";
import { playerpresent, personalizeExerciseForPlayer, gettype } from "../../components/funtionsBD/Mainservice";
import { usePermissions } from "../../context/Permissioncontext";
import { isOffTableExercise, isKotExercise } from "../../utils/teExecutionType";
import { useLiveTrainingPortalRoot } from "../../hooks/useLiveFullscreen";
import {
  attachPersonalizedToPlayer,
  isPersonalizedTrainingSlot,
} from "../../utils/personalizedTrainingSlot";
import PersonalizeExerciseDialog from "../../components/PersonalizeExerciseDialog";
import { LiveTrainingCoachTablesDialog } from "./LiveTrainingCoachTablesDialog";
import { LiveTrainingCoachCameraCalibration } from "./LiveTrainingCoachCameraCalibration";
import { LiveTrainingCoachChronoPanel } from "./LiveTrainingCoachChronoPanel";
import { LiveTrainingExerciseSetupDialog } from "./LiveTrainingExerciseSetupDialog";
import { LiveTrainingCoachCountPanel } from "./LiveTrainingCoachCountPanel";
import { KotPlayerLeaderboard } from "./KotPlayerPanels";
import { useCoachMediaCapture } from "../../hooks/useCoachMediaCapture";
import { useCoachInstructionRecording } from "../../hooks/useCoachInstructionRecording";
import { useCoachTableVideoRecording } from "../../hooks/useCoachTableVideoRecording";
import { CLOSE_TOUR_TRANSIENT_UI_EVENT } from "../../tour/tourEvents";
import { useCoachTableReplay } from "../../hooks/useCoachTableReplay";
import { LiveTrainingTableReplayReview } from "./LiveTrainingTableReplayReview";
import { LiveTrainingTableEvaluationPlayback } from "./LiveTrainingTableEvaluationPlayback";
import { useCoachTableEvaluationVideos } from "../../hooks/useCoachTableEvaluationVideos";
import { LiveTrainingRecordInstructionsDialog } from "./LiveTrainingRecordInstructionsDialog";
import {
  countCoachTablesWithBothPlayers,
  filterCoachConfiguredTables,
  filterPresentRoster,
  filterTablesForStation,
  getUnassignedPresentPlayers,
  kotSnapshotToCoachTables,
  resolveTableRoomCapacity,
} from "../../utils/coachSessionTables";
import { buildOrderedChronoPlayers } from "../../utils/coachChronoPlayers";
import {
  coachToolsFromExercise,
  isLiveTimerBlock,
} from "../../utils/teCoachTools";
import { stationLabel } from "../../utils/liveTrainingStations";
import {
  TE_STATE_FINALIZED,
  TE_STATE_PAUSED,
  TE_STATE_SCHEDULED,
  TE_STATE_STARTED,
} from "../../utils/coachExerciseNavigation";
import {
  canOpenSessionPresenceDialog,
  isSessionClosedForPresence,
  isTableConfigurationLockedAfterFinalize,
  shouldAutoSeatAfterPresenceChange,
} from "../../utils/sessionPresenceEdit";
import { userCanEditFinalizedExerciseSeating } from "../../utils/planningAccess";
import { SessionMediaFilesDialog } from "../../components/SessionMediaFilesDialog";
import { MoveEvaluationVideoDialog } from "../../components/MoveEvaluationVideoDialog";

import "../../components/KotSettings.css";
import "./CoachTablePreviewCard.css";
import "./LiveTrainingCoachDashboard.css";
import { LiveTrainingCoachVoiceHold } from "./LiveTrainingCoachVoiceHold";
import { FilmingConsentBar } from "./FilmingConsentBar";
import { DemoFinalizedReviewStrip } from "./DemoFinalizedReviewStrip";
import TimerOutlinedIcon from "@mui/icons-material/TimerOutlined";
import FormatListNumberedIcon from "@mui/icons-material/FormatListNumbered";
import VideocamOutlinedIcon from "@mui/icons-material/VideocamOutlined";
import {
  LIVE_EXERCISE_MODES,
  liveExerciseModeCopyKeys,
  liveModeRoleLabelOverride,
  resolveLiveExerciseMode,
} from "../../utils/liveExerciseMode";
import { useLiveModeSync } from "../../hooks/useLiveModeSync";
import { useCoachIntervalTimer } from "../../hooks/useCoachIntervalTimer";
import { useCoachMatchScoreboard } from "../../hooks/useCoachMatchScoreboard";
import { matchWinnerSide } from "../../utils/matchScoreboard";
import { LiveTrainingModeStrip } from "./LiveTrainingModeStrip";
import { LiveTrainingIntervalTimerPanel } from "./LiveTrainingIntervalTimerPanel";
import { LiveTrainingMatchScorePanel } from "./LiveTrainingMatchScorePanel";
import {
  getStationCameraTarget,
  resolveStationCameraKey,
} from "../../utils/stationCameraBinding";

const NO_TABLES = [];

export function LiveTrainingCoachDashboard({
  roster = [],
  exercise,
  exerciseStateId,
  isKot: isKotProp,
  socket,
  tsId,
  grId,
  focusedTsgId = null,
  stations = null,
  stationCamerasByTsgId = null,
  onSelectStation = null,
  kotSnapshot,
  kotRound: kotRoundProp = null,
  viewRefreshToken = 0,
  requestRefresh = null,
  actionError,
  onOpenFeedback,
  chatUnread = 0,
  onCoachStopRoundReady,
  onCoachTableFeedbackSent,
  coachUserId = null,
  user = null,
  brandPrimary = "#0877BC",
  brandSecondary = "#46528b",
  getHrSamples = null,
  getLiveBpm = null,
  requestHrSnapshot = null,
  liveSequenceRolesByTeId = {},
  socketSequenceMetaByTeId = {},
  currentSequenceIndexByTeId = {},
  onRosterUpdate = null,
  exercises = [],
  realtimeByTeId = {},
  awaitingManualSequenceStart = false,
  awaitingAutoSequenceStart = false,
  sequenceTransitionPauseRemaining = null,
  lastSequenceCompleted = false,
  onReviewFinalized = null,
  openPresenceTick = 0,
  openChronoTick = 0,
  tablesWithCamera = null,
}) {
  const { t, i18n } = useTranslation();
  const { hasPermission } = usePermissions();
  const { brand } = useBrand();
  const livePortalRoot = useLiveTrainingPortalRoot();
  const [tablesDialogOpen, setTablesDialogOpen] = useState(false);
  const [playerPresenceOpen, setPlayerPresenceOpen] = useState(false);
  const [leaderboardOpen, setLeaderboardOpen] = useState(false);
  const [guestQrOpen, setGuestQrOpen] = useState(false);
  const [voiceHoldTarget, setVoiceHoldTarget] = useState(null);
  const [resultActionMatchId, setResultActionMatchId] = useState(null);
  const [globalRecordingMode, setGlobalRecordingMode] = useState(null); // 'voice' | 'video' | null
  const [chronoModeOpen, setChronoModeOpen] = useState(false);
  const [exerciseSetupOpen, setExerciseSetupOpen] = useState(false);
  const [countModeOpen, setCountModeOpen] = useState(false);
  const [intervalOpen, setIntervalOpen] = useState(false);
  const [matchOpen, setMatchOpen] = useState(false);
  const [instructionsDialogOpen, setInstructionsDialogOpen] = useState(false);
  const [cameraCalibTable, setCameraCalibTable] = useState(null);
  const [personalizeOpen, setPersonalizeOpen] = useState(false);
  const [personalizePlayerId, setPersonalizePlayerId] = useState(null);
  const [exerciseTypes, setExerciseTypes] = useState([]);
  const prevRoundStateRef = useRef(null);
  const teId = exercise?.te_id;
  // Prefer the coach-selected station so presence/tables follow the tab even
  // before the focused exercise row has caught up.
  const stationTsgId =
    focusedTsgId != null && focusedTsgId !== ""
      ? String(focusedTsgId)
      : exercise?.tsg_id != null && exercise.tsg_id !== ""
        ? String(exercise.tsg_id)
        : null;
  const isKot = Boolean(isKotProp) || isKotExercise(exercise);
  const timerOnlyBlock = isLiveTimerBlock(exercise);
  const isOffTable =
    !isKot && (timerOnlyBlock || isOffTableExercise(exercise));
  const isPersonalizedSlot = isPersonalizedTrainingSlot(exercise);
  const coachLang = (i18n.language || "en").split("-")[0];
  const coachTools = useMemo(
    () => coachToolsFromExercise(exercise),
    [exercise]
  );
  const showChronoTool =
    (isOffTable || timerOnlyBlock) && coachTools.chrono;
  const liveMode = useMemo(() => resolveLiveExerciseMode(exercise), [exercise]);
  const isMultiballMode = liveMode.mode === LIVE_EXERCISE_MODES.MULTIBALL;
  const showOffTableCount = isOffTable && coachTools.count;
  const modeCountEnabled = !isOffTable && liveMode.tools.count;
  const showCountTool = showOffTableCount || modeCountEnabled;
  const countLabel =
    coachTools.count_label ||
    (modeCountEnabled && liveMode.countLabelKey ? t(liveMode.countLabelKey) : "");

  const liveModeSync = useLiveModeSync({
    socket,
    tsId,
    teId,
    enabled: liveMode.tools.interval || liveMode.tools.match,
  });
  const intervalTimer = useCoachIntervalTimer({
    teId,
    defaults: liveMode.intervalDefaults,
    enabled: liveMode.tools.interval,
    synced: liveModeSync.interval,
    publish: liveModeSync.publish,
  });

  const {
    activeRecording,
    startVoiceRecording,
    startVideoRecording,
    stopCapture,
  } = useCoachMediaCapture({
    socket,
    teId,
    tsId,
    coachUserId,
    onFeedbackSent: onCoachTableFeedbackSent,
  });

  const instructionRecording = useCoachInstructionRecording({
    socket,
    teId,
    tsId,
    coachUserId,
  });

  const tableVideoRecording = useCoachTableVideoRecording({
    socket,
    teId,
    tsId,
    coachUserId,
  });

  const stationCameraForFocus = useMemo(() => {
    const tsg =
      stationTsgId != null && String(stationTsgId).trim()
        ? String(stationTsgId)
        : null;
    if (!tsg) return null;
    const live = stationCamerasByTsgId?.[tsg] || null;
    if (live) return live;
    return getStationCameraTarget(resolveStationCameraKey({ tsgId: tsg }));
  }, [stationTsgId, stationCamerasByTsgId]);

  const isStarted = exerciseStateId === TE_STATE_STARTED;
  const isPaused = exerciseStateId === TE_STATE_PAUSED;
  const isFinalized = exerciseStateId === TE_STATE_FINALIZED;
  const isRunning = isStarted || isPaused;
  const canRecordInstructions =
    Boolean(teId) &&
    (!exerciseStateId || exerciseStateId === TE_STATE_SCHEDULED);

  useEffect(() => {
    if (!canRecordInstructions && !instructionRecording.recording) {
      setInstructionsDialogOpen(false);
    }
  }, [canRecordInstructions, instructionRecording.recording]);

  const handleTableVideoToggle = useCallback(
    (tn) => {
      const n = Number(tn);
      if (!Number.isFinite(n) || n <= 0) return;
      if (tableVideoRecording.isTableRecording?.(n)) {
        if (typeof tableVideoRecording.stopTable === "function") {
          tableVideoRecording.stopTable(n);
        } else {
          tableVideoRecording.stop?.();
        }
        return;
      }
      tableVideoRecording.resetError();
      tableVideoRecording.start?.(n);
    },
    [tableVideoRecording]
  );

  const handleStartTableVideo = useCallback(
    (tableNumbers) => {
      tableVideoRecording.resetError();
      tableVideoRecording.start?.(tableNumbers);
    },
    [tableVideoRecording]
  );

  const handleStopTableVideo = useCallback(
    (tn) => {
      const n = Number(tn);
      if (!Number.isFinite(n) || n <= 0) {
        tableVideoRecording.stop?.();
        return;
      }
      if (typeof tableVideoRecording.stopTable === "function") {
        tableVideoRecording.stopTable(n);
      } else {
        tableVideoRecording.stop?.();
      }
    },
    [tableVideoRecording]
  );

  const tableReplay = useCoachTableReplay({
    socket,
    teId,
    tsId,
    coachUserId,
    exerciseLive: isRunning,
  });

  const sessionMeta = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("rsessn") || "null");
    } catch {
      return null;
    }
  }, []);

  const resolvedGrId = useMemo(
    () => grId || sessionMeta?.gr_id || null,
    [grId, sessionMeta]
  );

  const grpInf = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("grpinf") || "null");
    } catch {
      return null;
    }
  }, []);

  const demoClub = isDemoClub(brand, grpInf?.groupDetails, user);

  useEffect(() => {
    if (socket && tsId) {
      emitCoachJoinTrainingSession(socket, tsId, teId);
    }
  }, [socket, tsId, teId]);

  const tableSetup = useCoachSessionTables({
    grId: resolvedGrId,
    tsId,
    teId,
    tsgId: stationTsgId,
    stations,
    roster,
    exercise,
    exercises,
    socket,
    viewRefreshToken,
    enabled: Boolean(resolvedGrId && tsId && teId),
  });

  const kotTableRows = useMemo(
    () => (kotSnapshot ? kotTableDisplayRows(kotSnapshot) : []),
    [kotSnapshot]
  );

  const roundState = kotSnapshot?.current_round?.round_state ?? null;

  const exerciseLocked = isRunning || isFinalized;

  // Coaches / monitors / moderators may correct seating after finalize.
  const canEditFinalizedTables = userCanEditFinalizedExerciseSeating(user);

  // Allow seating moves during a live block — coaches often need to fix
  // tables mid-session. Lock seating once finalized unless staff may edit.
  const tableConfigurationLocked = isTableConfigurationLockedAfterFinalize({
    isFinalized,
    canEditFinalizedSeating: canEditFinalizedTables,
  });

  const kotTablesAutoOpenRef = useRef(new Set());
  useEffect(() => {
    if (!isKot || !teId || tableConfigurationLocked) return;
    if (exercise?.ex_id) return;
    const teKey = String(teId);
    if (kotTablesAutoOpenRef.current.has(teKey)) return;
    kotTablesAutoOpenRef.current.add(teKey);
    setTablesDialogOpen(true);
  }, [isKot, teId, tableConfigurationLocked, exercise?.ex_id]);

  const showSeatingActions = !isOffTable && !tableConfigurationLocked;

  const selectedSession = useMemo(
    () =>
      sessionMeta?.ts_id
        ? sessionMeta
        : tsId
          ? { ts_id: tsId, gr_id: resolvedGrId }
          : null,
    [sessionMeta, tsId, resolvedGrId]
  );

  const showPlayerPresenceShortcut =
    hasPermission("PLAYER_ADD_TO_SESSION") ||
    hasPermission("PLAYER_REMOVE_FROM_SESSION");
  const playerPresenceDisabled = !canOpenSessionPresenceDialog({
    hasPresencePermission: showPlayerPresenceShortcut,
    exerciseStateId: exerciseStateId,
    sessionClosed: isSessionClosedForPresence(selectedSession),
    kotAllowsPresence: isKot,
  });
  const playerPresenceDisabledTitle = playerPresenceDisabled
    ? t("managePlayerPresence") || "Manage player presence"
    : isFinalized && canEditFinalizedTables
      ? t("presenceCorrectionHintModerator") ||
        "This exercise is finalized. You can correct presence and edit table seating."
      : isFinalized
        ? t("presenceCorrectionHint") ||
          "This exercise is finalized. You can still correct who was present or absent. Switch to the next exercise to add new players."
        : t("managePlayerPresence") || "Manage player presence";

  useEffect(() => {
    if (openPresenceTick) setPlayerPresenceOpen(true);
  }, [openPresenceTick]);

  useEffect(() => {
    if (openChronoTick && showChronoTool) setChronoModeOpen(true);
  }, [openChronoTick, showChronoTool]);

  const selectedgrp = useMemo(() => {
    const clubId =
      grpInf?.groupDetails?.club_id ||
      grpInf?.club_id ||
      sessionMeta?.club_id ||
      user?.club_id ||
      null;
    const sessionGrId = resolvedGrId || sessionMeta?.gr_id || null;
    if (grpInf?.groupDetails?.gr_id) {
      const details = { ...grpInf.groupDetails };
      if (clubId && !details.club_id) {
        details.club_id = clubId;
      }
      // Phone walk-ins seat on the live session group — not always the demo
      // group stored in grpinf from the coach dashboard.
      if (
        sessionGrId &&
        String(details.gr_id) !== String(sessionGrId)
      ) {
        details.gr_id = sessionGrId;
      }
      return { ...grpInf, groupDetails: details };
    }
    if (resolvedGrId) {
      return {
        groupDetails: {
          gr_id: resolvedGrId,
          ...(clubId ? { club_id: clubId } : {}),
        },
      };
    }
    return null;
  }, [grpInf, resolvedGrId, sessionMeta, user]);

  const handlePlayerPresenceUpdate = useCallback(async () => {
    try {
      await onRosterUpdate?.();
      await tableSetup.reload();
    } catch (e) {
      console.error("LiveTrainingCoachDashboard - roster update failed:", e);
    }
  }, [onRosterUpdate, tableSetup]);

  const seatPresentPlayers = useCallback(
    async (presentPlayers = []) => {
      if (
        !tsId ||
        !shouldAutoSeatAfterPresenceChange({
          sessionClosed: isFinalized,
          exerciseLive: tableConfigurationLocked && !isFinalized,
        })
      ) {
        return {
          ok: true,
          reason: isFinalized ? "session_finalized" : "locked",
        };
      }
      if (!Array.isArray(presentPlayers) || presentPlayers.length === 0) {
        return { ok: true, reason: "empty" };
      }

      try {
        // Empty session → seat by ranking. Already seated → fill next free seats.
        let result = await tableSetup.assignFromPresence(presentPlayers);
        // Marking several players quickly can hit the busy lock — retry briefly.
        if (result?.reason === "busy") {
          for (let i = 0; i < 3 && result?.reason === "busy"; i++) {
            await new Promise((r) => setTimeout(r, 350));
            result = await tableSetup.assignFromPresence(presentPlayers);
          }
        }
        if (result?.ok) return result;
        if (result?.reason === "no_seats") {
          return {
            ...result,
            message:
              t("noFreeTableSeatsForPresentPlayers") ||
              "No free table seats for newly present players. Free a seat or open table setup.",
          };
        }
        if (result?.reason === "busy") {
          return {
            ...result,
            message:
              t("tableAssignmentInProgress") ||
              "Table assignment still in progress. Try Done again in a moment.",
          };
        }
        return {
          ...result,
          message:
            result?.message ||
            tableSetup.error ||
            t("presenceSeatAssignFailed") ||
            "Could not seat newly present players.",
        };
      } catch (e) {
        console.error("LiveTrainingCoachDashboard - presence seat assign failed:", e);
        return {
          ok: false,
          reason: "error",
          message:
            e?.message ||
            t("presenceSeatAssignFailed") ||
            "Could not seat newly present players.",
        };
      }
    },
    [tsId, tableSetup, tableConfigurationLocked, isFinalized, t]
  );

  const handlePlayerMarkedPresent = useCallback(
    async ({ presentPlayers = [] } = {}) => seatPresentPlayers(presentPlayers),
    [seatPresentPlayers]
  );

  const handlePresenceDone = useCallback(
    async ({ presentPlayers = [] } = {}) => seatPresentPlayers(presentPlayers),
    [seatPresentPlayers]
  );

  const kotRoundInternal = useKotCoachRound({
    socket,
    tsId,
    teId,
    exerciseStateId,
    externalSnapshot: kotSnapshot,
    enabled: Boolean(!kotRoundProp && socket && tsId && teId && isKot),
    stopRoundMode: "coach",
  });
  const kotRound = kotRoundProp || kotRoundInternal;

  const kotSubmit = useKotCoachSubmitResult({
    socket,
    tsId,
    teId,
    snapshot: kotSnapshot,
    enabled: Boolean(socket && tsId && teId),
    submitterMode: "coach",
    onSubmitted: () => {},
  });
  const kotSubmitCloseRef = useRef(kotSubmit.close);
  kotSubmitCloseRef.current = kotSubmit.close;

  const kotRuntimeRows = useMemo(
    () =>
      kotTableRows.filter(
        (m) =>
          isRealKotMatchId(m.kot_match_id) ||
          m.is_lone_waiting ||
          (m.player_a_id && m.player_b_id && m.table_number != null)
      ),
    [kotTableRows]
  );

  const openScoreWizard = useCallback(
    (match) => {
      if (!match || !isRealKotMatchId(match.kot_match_id)) return;
      if (match.match_state === "WON" || match.match_state === "ABANDONED") {
        return;
      }
      const needsStopOverride =
        roundState === "STOP_REQUESTED" ||
        coachMustStopRoundBeforeResult({
          config: kotSnapshot?.config,
          roundState,
        });
      kotSubmit.openForMatch(match, {
        roundStateOverride: needsStopOverride ? "STOP_REQUESTED" : undefined,
      });
    },
    [kotSubmit, kotSnapshot?.config, roundState]
  );

  useEffect(() => {
    if (
      roundState === "STOP_REQUESTED" &&
      prevRoundStateRef.current === "IN_PROGRESS" &&
      isKot &&
      exerciseLocked
    ) {
      setTablesDialogOpen(true);
    }
    prevRoundStateRef.current = roundState;
  }, [roundState, isKot, exerciseLocked]);

  const sessionPresentRoster = useMemo(
    () => filterPresentRoster(roster),
    [roster]
  );

  // Station pool: present players who are not seated on another station.
  // Use Array.isArray so an empty station pool is not replaced by session roster.
  const presentRoster = useMemo(
    () =>
      Array.isArray(tableSetup.stationPresentRoster)
        ? tableSetup.stationPresentRoster
        : sessionPresentRoster,
    [tableSetup.stationPresentRoster, sessionPresentRoster]
  );

  const unseatedPresentPlayers = useMemo(() => {
    const tables =
      tableSetup.stationRawTables?.length > 0
        ? tableSetup.stationRawTables
        : tableSetup.rawTables || [];
    return getUnassignedPresentPlayers(presentRoster, tables);
  }, [
    presentRoster,
    tableSetup.stationRawTables,
    tableSetup.rawTables,
  ]);

  const otherStationPresentPlayers = tableSetup.otherStationPresentPlayers || [];

  const stationLabelsById = useMemo(() => {
    const map = {};
    for (const station of stations || []) {
      if (station?.tsg_id == null) continue;
      map[String(station.tsg_id)] = stationLabel(station, t);
    }
    return map;
  }, [stations, t]);
  const roomCapacity = useMemo(() => {
    const stationTables = tableSetup.stationRawTables;
    if (Array.isArray(stationTables) && stationTables.length > 0) {
      return stationTables.length;
    }
    return resolveTableRoomCapacity({
      rawTables: tableSetup.rawTables,
      session: sessionMeta,
      grpInf,
    });
  }, [
    tableSetup.stationRawTables,
    tableSetup.rawTables,
    sessionMeta,
    grpInf,
  ]);

  const showKotMatches =
    isKot &&
    exerciseLocked &&
    (roundState === "IN_PROGRESS" || roundState === "STOP_REQUESTED");

  const kotConfigTables = useMemo(() => {
    if (!isKot || !kotSnapshot || showKotMatches) return null;
    const rows = kotSnapshotToCoachTables(
      kotSnapshot,
      roster,
      exercise,
      null
    );
    if (rows.length === 0) return null;
    if (!stationTsgId) return rows;
    const scoped = filterTablesForStation(rows, stationTsgId, stations);
    return scoped.length > 0 ? scoped : rows;
  }, [isKot, kotSnapshot, showKotMatches, roster, exercise, stationTsgId, stations]);

  const kotSnapshotRevision = kotSnapshot?.kot_snapshot_revision ?? null;

  useEffect(() => {
    if (!isKot || showKotMatches || kotSnapshotRevision == null) return;
    tableSetup.reload();
  }, [isKot, showKotMatches, kotSnapshotRevision, tableSetup.reload]);

  const coachTableRows = useMemo(() => {
    if (showKotMatches) return tableSetup.tables;
    if (tableSetup.loading && kotConfigTables) return kotConfigTables;
    if (tableSetup.tables.length > 0) return tableSetup.tables;
    return kotConfigTables || tableSetup.tables;
  }, [showKotMatches, tableSetup.loading, tableSetup.tables, kotConfigTables]);

  useEffect(() => {
    setChronoModeOpen(false);
    setCountModeOpen(false);
    setIntervalOpen(false);
    setMatchOpen(false);
  }, [teId]);

  const assignedCount = useMemo(() => {
    if (tableSetup.assignedIds instanceof Set) {
      return tableSetup.assignedIds.size;
    }
    const ids = new Set();
    for (const table of coachTableRows) {
      if (table.player1?.playerId != null) ids.add(String(table.player1.playerId));
      if (table.player2?.playerId != null) ids.add(String(table.player2.playerId));
      if (table.player3?.playerId != null) ids.add(String(table.player3.playerId));
      for (const extra of table.extras || []) {
        if (extra?.playerId != null) ids.add(String(extra.playerId));
      }
    }
    return ids.size;
  }, [coachTableRows, tableSetup.assignedIds]);

  const usedTableCount = useMemo(
    () => countCoachTablesWithBothPlayers(coachTableRows),
    [coachTableRows]
  );

  // Include empty station tables so the config dialog can seat unassigned players.
  const activeTables = useMemo(() => {
    const rows = coachTableRows;
    const isRunning =
      exerciseStateId === TE_STATE_STARTED ||
      exerciseStateId === TE_STATE_PAUSED;
    const teKey = teId != null ? String(teId) : null;
    const liveRoles = teKey ? liveSequenceRolesByTeId[teKey] : null;
    const sequenceMeta = teKey ? socketSequenceMetaByTeId[teKey] : null;
    const seqIdx = teKey ? currentSequenceIndexByTeId[teKey] : null;

    return rows.map((table) => {
      const sideRoles = resolveCoachTableSideRoles(exercise, table.tableNumber, {
        isExerciseRunning: isRunning,
        liveRoles,
        sequenceMeta,
        currentSequenceIndex: seqIdx,
      });
      const withDrill = (player) => {
        if (!player) return null;
        let role =
          player === table.player1
            ? sideRoles.player_a_role ?? player.role ?? null
            : player === table.player2
              ? sideRoles.player_b_role ?? player.role ?? null
              : player.role ?? null;
        // Multiball blocks planned before sequences existed: side A performs, side B feeds.
        if (isMultiballMode && !role) {
          role = player === table.player1 ? "active" : "sparring";
        }
        const next = { ...player, role };
        const roleLabel = liveModeRoleLabelOverride(liveMode, role, t);
        if (roleLabel) next.roleLabel = roleLabel;
        return isPersonalizedSlot
          ? attachPersonalizedToPlayer(next, exercise, coachLang)
          : next;
      };
      let player3 = table.player3;
      if (player3 && isMultiballMode) {
        player3 = {
          ...player3,
          roleLabel: t("liveModeRoleCollector", "Collecting balls"),
        };
      }
      return {
        ...table,
        player1: withDrill(table.player1),
        player2: withDrill(table.player2),
        player3: player3
          ? isPersonalizedSlot
            ? attachPersonalizedToPlayer(player3, exercise, coachLang)
            : player3
          : player3,
      };
    });
  }, [
    liveMode,
    isMultiballMode,
    t,
    coachTableRows,
    exercise,
    exerciseStateId,
    teId,
    liveSequenceRolesByTeId,
    socketSequenceMetaByTeId,
    currentSequenceIndexByTeId,
    isPersonalizedSlot,
    coachLang,
  ]);

  const configuredTables = useMemo(
    () => filterCoachConfiguredTables(activeTables),
    [activeTables]
  );

  const chronoPlayers = useMemo(
    () => {
      let rows =
        isOffTable || showCountTool
          ? buildOrderedChronoPlayers(configuredTables, {
              presentRoster,
              tsgId: stationTsgId,
            })
          : [];
      if (!isOffTable) {
        const seated = rows.filter((p) => p.tableNumber != null);
        if (seated.length > 0) rows = seated;
      }
      if (!isPersonalizedSlot) return rows;
      return rows.map((p) => attachPersonalizedToPlayer(p, exercise, coachLang));
    },
    [
      isOffTable,
      showCountTool,
      configuredTables,
      presentRoster,
      stationTsgId,
      isPersonalizedSlot,
      exercise,
      coachLang,
    ]
  );

  const matchScoreboard = useCoachMatchScoreboard({
    teId,
    seatedTables: liveMode.tools.match ? configuredTables : NO_TABLES,
    defaultBestOf: liveMode.defaultBestOf,
    enabled: liveMode.tools.match,
    synced: liveModeSync.match,
    publish: liveModeSync.publish,
  });
  const matchSummary = useMemo(() => {
    const tables = matchScoreboard.board.tables;
    if (!liveMode.tools.match || tables.length === 0) return null;
    const done = tables.filter(
      (tbl) => matchWinnerSide(tbl, matchScoreboard.board.bestOf) != null
    ).length;
    return `${done}/${tables.length}`;
  }, [liveMode.tools.match, matchScoreboard.board]);

  useEffect(() => {
    if (tablesDialogOpen) {
      requestHrSnapshot?.();
    }
  }, [tablesDialogOpen, requestHrSnapshot]);

  const handleCoachEnterResult = useCallback(
    (match) => {
      if (!match || !isRealKotMatchId(match.kot_match_id)) return;
      if (!canCoachShowResultButton({ match, roundState })) return;

      const afterStop = (err) => {
        setResultActionMatchId(null);
        if (err) return;
        setTablesDialogOpen(true);
        openScoreWizard(match);
      };

      setResultActionMatchId(match.kot_match_id);

      if (roundState === "STOP_REQUESTED") {
        setResultActionMatchId(null);
        setTablesDialogOpen(true);
        openScoreWizard(match);
        return;
      }

      if (
        coachMustStopRoundBeforeResult({
          config: kotSnapshot?.config,
          roundState,
        })
      ) {
        kotRound.requestStopRoundForMatch(match, afterStop);
        return;
      }

      setResultActionMatchId(null);
      openScoreWizard(match);
    },
    [kotRound, kotSnapshot?.config, roundState, openScoreWizard]
  );

  const handleCoachStopRound = useCallback(() => {
    if (roundState === "STOP_REQUESTED") {
      setTablesDialogOpen(true);
      return;
    }
    const match = pickMatchForKotStopRequest(kotSnapshot);
    if (!match?.kot_match_id || !isRealKotMatchId(match.kot_match_id)) {
      kotRound.requestStopRound();
      return;
    }
    setResultActionMatchId(match.kot_match_id);
    kotRound.requestStopRoundForMatch(match, (err) => {
      setResultActionMatchId(null);
      if (!err) setTablesDialogOpen(true);
    });
  }, [kotSnapshot, kotRound, roundState]);

  useEffect(() => {
    onCoachStopRoundReady?.(handleCoachStopRound);
    return () => onCoachStopRoundReady?.(null);
  }, [handleCoachStopRound, onCoachStopRoundReady]);

  const handlePlayerVoiceHold = useCallback(
    (player) => {
      if (isFinalized || !player?.userId || !socket || !teId || !coachUserId) {
        return;
      }
      setVoiceHoldTarget({
        userId: String(player.userId),
        name: player.name || t("player"),
      });
    },
    [isFinalized, socket, teId, coachUserId, t]
  );

  const personalizeGroupPlayers = useMemo(
    () =>
      (roster || []).map((p) => ({
        player_id: p.player_id,
        user_id: p.user_id,
        user_first_name: p.user_first_name || p.player_name,
        user_last_name: p.user_last_name,
        user_alias: p.user_alias,
        user_image: p.user_image,
      })),
    [roster]
  );

  const handleEditPersonalizedPlayer = useCallback(
    (player) => {
      const pid = player?.playerId || player?.player_id;
      if (!pid || !teId) return;
      setPersonalizePlayerId(pid);
      setPersonalizeOpen(true);
      if (!exerciseTypes.length) {
        gettype()
          .then((res) => setExerciseTypes(res?.data?.data || []))
          .catch(() => {});
      }
    },
    [teId, exerciseTypes.length]
  );

  const handlePersonalizedExerciseSelected = useCallback(
    async (recommendedEx, playerId) => {
      if (!recommendedEx?.ex_id || !teId || !playerId) return;
      try {
        await personalizeExerciseForPlayer({
          te_id: teId,
          player_id: playerId,
          ex_id: recommendedEx.ex_id,
        });
        setPersonalizeOpen(false);
        setPersonalizePlayerId(null);
        requestRefresh?.();
      } catch (err) {
        console.error("personalizeExerciseForPlayer:", err);
      }
    },
    [teId, requestRefresh]
  );

  const kotPlayerCell = useCallback(
    (player, playerId) => {
      const fields = resolvePlayerDisplayFields(player, roster, playerId);
      return {
        name: fields.name,
        userId: fields.userId,
        playerId: fields.playerId,
      };
    },
    [roster]
  );

  const closeTablesDialog = useCallback(() => {
    if (!activeRecording) {
      setGlobalRecordingMode(null);
    }
    setTablesDialogOpen(false);
  }, [activeRecording]);

  const tableEvalVideos = useCoachTableEvaluationVideos({
    tsId,
    teId,
    enabled: isFinalized && tablesDialogOpen,
    tables: coachTableRows,
  });
  const reloadTableEvalVideos = tableEvalVideos.reload;
  const [evalPlaybackTable, setEvalPlaybackTable] = useState(null);
  const [sessionMediaOpen, setSessionMediaOpen] = useState(false);
  const [moveVideoTarget, setMoveVideoTarget] = useState(null);
  useEffect(() => {
    setEvalPlaybackTable(null);
    setMoveVideoTarget(null);
  }, [teId]);

  const handleMoveEvalVideo = useCallback((clip) => {
    if (!clip?.message_id) return;
    setMoveVideoTarget({
      message_id: clip.message_id,
      te_id: clip.te_id,
      table_id: clip.table_id,
      sender_id: clip.sender_id || null,
      message_type: "evaluation",
      video_message_url: clip.video_url || clip.video_message_url,
      text_message: clip.text_message,
      view_setup: clip.view_setup,
      sequence_number: clip.sequence_number ?? clip.sequence_order,
      createdAt: clip.createdAt,
      is_kot: clip.is_kot,
      kind: clip.kind,
      kot_match_id: clip.kot_match_id,
    });
  }, []);

  const handleEvalVideoMoved = useCallback(async () => {
    setMoveVideoTarget(null);
    setEvalPlaybackTable(null);
    await reloadTableEvalVideos?.();
  }, [reloadTableEvalVideos]);

  useEffect(() => {
    const dismissTourDialogs = () => {
      setTablesDialogOpen(false);
      setPlayerPresenceOpen(false);
      setLeaderboardOpen(false);
      setGuestQrOpen(false);
      setVoiceHoldTarget(null);
      setResultActionMatchId(null);
      setChronoModeOpen(false);
      setCountModeOpen(false);
      setIntervalOpen(false);
      setMatchOpen(false);
      setExerciseSetupOpen(false);
      setInstructionsDialogOpen(false);
      try {
        kotSubmitCloseRef.current?.();
      } catch {
        /* ignore */
      }
    };
    window.addEventListener(CLOSE_TOUR_TRANSIENT_UI_EVENT, dismissTourDialogs);
    return () => window.removeEventListener(CLOSE_TOUR_TRANSIENT_UI_EVENT, dismissTourDialogs);
  }, []);

  const tableCapacity = roomCapacity || tableSetup.tables.length;

  return (
    <div
      className="lt-coach-dash"
      data-tour-id="live-coach-floor"
      style={{
        "--lt-brand": brandPrimary,
        "--lt-brand-secondary": brandSecondary,
      }}
    >
      {actionError ? (
        <p className="lt-coach-dash__error" role="alert">
          {actionError}
        </p>
      ) : null}

      {demoClub ? (
        <DemoGuestInviteQrOverlay
          open={guestQrOpen}
          onClose={() => setGuestQrOpen(false)}
        />
      ) : null}
      {demoClub ? (
        <DemoFinalizedReviewStrip
          exercises={exercises}
          realtimeByTeId={realtimeByTeId}
          currentTeId={teId}
          currentSequenceIndexByTeId={currentSequenceIndexByTeId}
          awaitingManualSequenceStart={awaitingManualSequenceStart}
          awaitingAutoSequenceStart={awaitingAutoSequenceStart}
          lastSequenceCompleted={lastSequenceCompleted}
          onSelect={onReviewFinalized}
        />
      ) : null}

      {kotRound.stopRoundError ? (
        <p className="lt-coach-dash__error" role="alert">
          {t(kotRound.stopRoundError) || kotRound.stopRoundError}
        </p>
      ) : null}

      <div className="lt-coach-dash__toolbar">
        <div className="lt-coach-dash__stats">
          {isOffTable ? null : (
          <div className="lt-coach-dash__stat">
            <TableRestaurantIcon fontSize="small" />
            <span>
              {usedTableCount}/{tableCapacity} {t("tables") || "Tables"}
            </span>
          </div>
          )}
          <div className="lt-coach-dash__stat">
            <GroupsIcon fontSize="small" />
            <span>
              {assignedCount}/
              {Math.max(presentRoster.length, assignedCount)}{" "}
              {t("players") || "players"}
              {sessionPresentRoster.length > presentRoster.length ? (
                <span className="lt-coach-dash__stat-muted">
                  {" "}
                  · {sessionPresentRoster.length}{" "}
                  {t("session") || "session"}
                </span>
              ) : null}
            </span>
          </div>
        </div>

        <div className="lt-coach-dash__toolbar-actions">
          <div className="lt-coach-dash__action-group" role="group">
            {isOffTable ? (
              <button
                type="button"
                className="lt-coach-dash__tables-btn"
                data-tour-id="live-coach-exercise-setup"
                onClick={() => setExerciseSetupOpen(true)}
                title={t("liveTrainingExerciseSetup", "Exercise setup")}
              >
                <EditIcon fontSize="small" />
                {t("liveTrainingExerciseSetup", "Exercise setup")}
              </button>
            ) : (
              <button
                type="button"
                className="lt-coach-dash__tables-btn"
                data-tour-id="live-coach-tables"
                onClick={() => setTablesDialogOpen(true)}
                title={
                  t("liveTrainingCoachTablesTitle") || "Table configuration"
                }
              >
                <ViewModuleIcon fontSize="small" />
                {t("liveTrainingCoachTablesBtn") || "Tables"}
              </button>
            )}

            {showPlayerPresenceShortcut ? (
              <button
                type="button"
                className="lt-coach-dash__setup-icon-btn"
                data-tour-id="live-coach-add-player"
                onClick={() => {
                  if (!playerPresenceDisabled) setPlayerPresenceOpen(true);
                }}
                disabled={playerPresenceDisabled}
                title={playerPresenceDisabledTitle}
                aria-label={t("addPlayer") || "Add player"}
              >
                <PersonAddAlt1Icon fontSize="small" />
              </button>
            ) : null}

            {demoClub ? (
              <DemoGuestInviteQrToggleButton
                open={guestQrOpen}
                onToggle={() => setGuestQrOpen((open) => !open)}
                className="lt-coach-dash__qr-btn"
              />
            ) : null}
          </div>

          {showChronoTool ||
          showOffTableCount ||
          canRecordInstructions ||
          isKot ? (
            <div className="lt-coach-dash__action-group" role="group">
              {showChronoTool ? (
                <button
                  type="button"
                  className={`lt-coach-dash__setup-btn lt-coach-dash__chrono-btn${
                    chronoModeOpen ? " lt-coach-dash__chrono-btn--active" : ""
                  }`}
                  data-tour-id="live-coach-chrono"
                  onClick={() => setChronoModeOpen(true)}
                  title={
                    t("liveTrainingChronoModeActivate") || "Record chrono mode"
                  }
                  aria-pressed={chronoModeOpen}
                >
                  <TimerOutlinedIcon fontSize="small" />
                  {t("liveTrainingChronoModeBtn") || "Chrono"}
                </button>
              ) : null}

              {showOffTableCount ? (
                <button
                  type="button"
                  className={`lt-coach-dash__setup-btn lt-coach-dash__chrono-btn${
                    countModeOpen ? " lt-coach-dash__chrono-btn--active" : ""
                  }`}
                  data-tour-id="live-coach-count"
                  onClick={() => setCountModeOpen(true)}
                  title={t("liveTrainingCountModeActivate") || "Count drills"}
                  aria-pressed={countModeOpen}
                >
                  <FormatListNumberedIcon fontSize="small" />
                  {t("liveTrainingCountModeBtn") || "Count"}
                </button>
              ) : null}

              {canRecordInstructions ? (
                <button
                  type="button"
                  className={`lt-coach-dash__setup-btn lt-coach-dash__chrono-btn${
                    instructionsDialogOpen || instructionRecording.recording
                      ? " lt-coach-dash__chrono-btn--active"
                      : ""
                  }`}
                  data-tour-id="live-coach-record-instructions"
                  onClick={() => setInstructionsDialogOpen(true)}
                  title={t(
                    "liveTrainingRecordInstructions",
                    "Record instructions"
                  )}
                  aria-pressed={
                    instructionsDialogOpen || instructionRecording.recording
                  }
                >
                  <VideocamOutlinedIcon fontSize="small" />
                  {t("liveTrainingRecordInstructionsBtn", "Instructions")}
                </button>
              ) : null}

              {isKot ? (
                <button
                  type="button"
                  className="lt-coach-dash__setup-btn lt-coach-dash__leaderboard-btn"
                  onClick={() => setLeaderboardOpen(true)}
                  title={t("kotLeaderboard") || t("classement")}
                  aria-label={t("kotLeaderboard") || t("classement")}
                >
                  <EmojiEventsIcon fontSize="small" />
                  {t("kotLeaderboard") || t("classement")}
                </button>
              ) : null}
            </div>
          ) : null}

          <div className="lt-coach-dash__action-group" role="group">
            <FilmingConsentBar teId={teId} />
            {onOpenFeedback ? (
              <button
                type="button"
                className="lt-coach-dash__setup-icon-btn lt-coach-dash__feedback-btn"
                onClick={onOpenFeedback}
                aria-label={t("liveTrainingFeedbackCoachBtn")}
                title={t("liveTrainingFeedbackCoachBtn")}
              >
                <span className="lt-coach-dash__feedback-icon-wrap">
                  <DigipingMark size={22} />
                  {chatUnread > 0 ? (
                    <span
                      className="lt-fab__badge"
                      aria-label={`${chatUnread} unread`}
                    >
                      {chatUnread > 9 ? "9+" : chatUnread}
                    </span>
                  ) : null}
                </span>
              </button>
            ) : null}
          </div>
        </div>
      </div>

      <LiveTrainingModeStrip
        profile={liveMode}
        t={t}
        interval={liveMode.tools.interval ? intervalTimer.state : null}
        intervalRemainingSec={intervalTimer.remainingSec}
        matchSummary={matchSummary}
        showCountAction={modeCountEnabled}
        countLabel={countLabel}
        onOpenInterval={() => setIntervalOpen(true)}
        onOpenMatch={() => setMatchOpen(true)}
        onOpenCount={() => setCountModeOpen(true)}
      />

      {isKot && roundState === "STOP_REQUESTED" && exerciseLocked && !tablesDialogOpen ? (
        <div className="lt-coach-dash__kot-bar">
          <button
            type="button"
            className="lt-coach-dash__tables-btn lt-coach-dash__tables-btn--inline"
            onClick={() => setTablesDialogOpen(true)}
          >
            <EditIcon fontSize="small" />
            {t("kotCoachScoreEntryReopen") || "Enter round results"}
          </button>
        </div>
      ) : null}

      <KotPlayerLeaderboard
        open={leaderboardOpen}
        rows={kotSnapshot?.leaderboard || []}
        matches={kotSnapshot?.matches || []}
        roster={roster}
        onDismiss={() => setLeaderboardOpen(false)}
        autoDismissMs={0}
        maxRows={0}
      />

      {isOffTable ? (
        <LiveTrainingExerciseSetupDialog
          open={exerciseSetupOpen}
          onClose={() => setExerciseSetupOpen(false)}
          exercise={exercise}
          tsId={tsId}
          onSaved={() => requestRefresh?.()}
        />
      ) : null}

      <LiveTrainingCoachTablesDialog
        open={tablesDialogOpen}
        onClose={closeTablesDialog}
        isKot={isKot}
        showKotMatches={showKotMatches}
        roundState={roundState}
        activeTables={activeTables}
        kotMatches={kotRuntimeRows}
        roster={roster}
        usedTableCount={usedTableCount}
        roomCapacity={tableCapacity}
        assignedCount={assignedCount}
        presentCount={Math.max(presentRoster.length, assignedCount)}
        sessionPresentCount={sessionPresentRoster.length}
        unseatedPlayers={unseatedPresentPlayers}
        otherStationPlayers={otherStationPresentPlayers}
        stationLabelsById={stationLabelsById}
        stations={stations}
        focusedTsgId={focusedTsgId}
        onSelectStation={onSelectStation}
        realtimeByTeId={realtimeByTeId}
        loading={tableSetup.loading}
        kotSubmit={kotSubmit}
        kotRound={kotRound}
        kotSnapshot={kotSnapshot}
        resultActionMatchId={resultActionMatchId}
        activeMatchId={kotSubmit.activeMatch?.kot_match_id}
        onMatchEnterResult={handleCoachEnterResult}
        onMatchScoreEntry={(match) => {
          setTablesDialogOpen(true);
          openScoreWizard(match);
        }}
        tableSetup={tableSetup}
        seatingActionsEnabled={showSeatingActions}
        exerciseLocked={tableConfigurationLocked}
        onOpenPlayerPresence={() => {
          if (!playerPresenceDisabled) setPlayerPresenceOpen(true);
        }}
        showPlayerPresenceShortcut={showPlayerPresenceShortcut}
        playerPresenceDisabled={playerPresenceDisabled}
        playerPresenceDisabledTitle={playerPresenceDisabledTitle}
        showSessionMediaShortcut={isFinalized && canEditFinalizedTables}
        onOpenSessionMedia={() => setSessionMediaOpen(true)}
        formatPlayerCell={kotPlayerCell}
        voiceHoldEnabled={Boolean(teId && !isFinalized)}
        onPlayerVoiceHold={handlePlayerVoiceHold}
        globalRecordingMode={globalRecordingMode}
        onRecordingModeChange={setGlobalRecordingMode}
        activeRecording={activeRecording}
        onStartVoiceRecording={startVoiceRecording}
        onStartVideoRecording={startVideoRecording}
        onStopCapture={stopCapture}
        tableVideoRecording={tableVideoRecording}
        tableVideoError={tableVideoRecording.error}
        onToggleTableVideoRecording={handleTableVideoToggle}
        onStartTableVideoRecording={handleStartTableVideo}
        onStopTableVideoRecording={handleStopTableVideo}
        onCalibrateCamera={({ tableNumber }) =>
          setCameraCalibTable(Number(tableNumber))
        }
        tablesWithCamera={tablesWithCamera}
        replayEnabled={tableReplay.enabled}
        armedByTable={tableReplay.armedByTable}
        evalRecordingByTable={tableReplay.evalRecordingByTable}
        pendingByTable={tableReplay.pendingByTable}
        noticeByTable={tableReplay.noticeByTable}
        onReplayTable={({ tableNumber }) =>
          tableReplay.requestReplay({ tableNumber })
        }
        evaluationVideosByTable={
          isFinalized ? tableEvalVideos.clipsByTable : null
        }
        onPlayEvaluationVideo={({ tableNumber }) =>
          setEvalPlaybackTable(tableNumber)
        }
        user={user}
        tsId={tsId}
        teId={teId}
        socket={socket}
        grId={resolvedGrId}
        pingfloVoiceDisabled={isFinalized || tableSetup.busy}
        presentPlayers={presentRoster}
        onVoiceCommand={async (cmd) => {
          if (!cmd?.type) return false;
          if (cmd.type === "rotate") {
            return tableSetup.rotate(cmd.direction);
          }
          if (cmd.type === "sort_ranking") {
            return tableSetup.assignByRanking(Boolean(cmd.ascending));
          }
          if (cmd.type === "switch") {
            return tableSetup.swapPlayers(
              cmd.fromTable,
              cmd.fromSlot,
              cmd.toTable,
              cmd.toSlot
            );
          }
          if (cmd.type === "move") {
            const result = await tableSetup.movePlayerForce(
              cmd.playerId,
              cmd.tableNumber
            );
            return Boolean(result?.ok);
          }
          if (cmd.type === "mark_present") {
            const playerId = cmd.playerId || cmd.player?.player_id;
            if (!tsId || !playerId) return false;
            try {
              await playerpresent({
                ts_id: tsId,
                gr_id: resolvedGrId || null,
                ts_present: true,
                player_id: playerId,
              });
              await handlePlayerPresenceUpdate();
              const nextPresent = [
                ...presentRoster.filter(
                  (p) =>
                    String(p.player_id || p.playerId) !== String(playerId)
                ),
                cmd.player || { player_id: playerId, presensee: true },
              ];
              await seatPresentPlayers(nextPresent);
              return true;
            } catch (e) {
              console.error(
                "LiveTrainingCoachDashboard - voice mark_present failed:",
                e
              );
              return false;
            }
          }
          return false;
        }}
        onPingfloSeatingChanged={async () => {
          await tableSetup.reload?.();
          if (typeof requestRefresh === "function") {
            try {
              await requestRefresh();
            } catch {
              /* ignore refresh errors — tables already reloaded */
            }
          }
        }}
        getHrSamples={getHrSamples}
        getLiveBpm={getLiveBpm}
        exercise={exercise}
        exerciseStateId={exerciseStateId}
        awaitingManualSequenceStart={awaitingManualSequenceStart}
        awaitingAutoSequenceStart={awaitingAutoSequenceStart}
        sequenceTransitionPauseRemaining={sequenceTransitionPauseRemaining}
        currentSequenceIndex={
          teId != null ? currentSequenceIndexByTeId[String(teId)] : null
        }
        onEditPersonalizedPlayer={
          isPersonalizedSlot ? handleEditPersonalizedPlayer : null
        }
      />

      <LiveTrainingCoachCameraCalibration
        open={cameraCalibTable != null}
        tableNumber={cameraCalibTable}
        socket={socket}
        tsId={tsId}
        onClose={() => setCameraCalibTable(null)}
      />

      {showChronoTool ? (
        <LiveTrainingCoachChronoPanel
          open={chronoModeOpen}
          onClose={() => setChronoModeOpen(false)}
          teId={teId}
          players={chronoPlayers}
          onOpenPlayerPresence={() => {
            if (!playerPresenceDisabled) setPlayerPresenceOpen(true);
          }}
          showPlayerPresenceShortcut={showPlayerPresenceShortcut}
          playerPresenceDisabled={playerPresenceDisabled}
          playerPresenceDisabledTitle={playerPresenceDisabledTitle}
          tsgId={stationTsgId}
          grId={resolvedGrId}
          tsId={tsId}
          socket={socket}
          coachUserId={coachUserId}
          stations={stations}
          stationCameraTarget={stationCameraForFocus}
          captureAllCameras={timerOnlyBlock}
        />
      ) : null}

      {showCountTool ? (
        <LiveTrainingCoachCountPanel
          open={countModeOpen}
          onClose={() => setCountModeOpen(false)}
          teId={teId}
          players={chronoPlayers}
          countLabel={countLabel}
          eyebrow={
            isOffTable
              ? null
              : t(liveExerciseModeCopyKeys(liveMode).titleKey)
          }
          tsgId={stationTsgId}
          grId={resolvedGrId}
          tsId={tsId}
          socket={socket}
        />
      ) : null}

      {liveMode.tools.interval ? (
        <LiveTrainingIntervalTimerPanel
          open={intervalOpen}
          onClose={() => setIntervalOpen(false)}
          timer={intervalTimer}
          t={t}
          brandPrimary={brandPrimary}
          brandSecondary={brandSecondary}
        />
      ) : null}

      {liveMode.tools.match ? (
        <LiveTrainingMatchScorePanel
          open={matchOpen}
          onClose={() => setMatchOpen(false)}
          scoreboard={matchScoreboard}
          teId={teId}
          variant={liveMode.variant}
          t={t}
          brandPrimary={brandPrimary}
          brandSecondary={brandSecondary}
        />
      ) : null}

      <LiveTrainingTableReplayReview
        clips={tableReplay.clips}
        busy={tableReplay.busy}
        onClose={tableReplay.closeReview}
        onSave={tableReplay.saveClip}
        onDelete={tableReplay.deleteClip}
        brandPrimary={brandPrimary}
        brandSecondary={brandSecondary}
        t={t}
      />

      {evalPlaybackTable != null ? (
        <LiveTrainingTableEvaluationPlayback
          tableNumber={evalPlaybackTable}
          clips={tableEvalVideos.clipsByTable[String(evalPlaybackTable)] || []}
          onClose={() => setEvalPlaybackTable(null)}
          brandPrimary={brandPrimary}
          brandSecondary={brandSecondary}
          t={t}
          canMoveVideo={canEditFinalizedTables}
          onMoveVideo={handleMoveEvalVideo}
        />
      ) : null}

      {canEditFinalizedTables ? (
        <SessionMediaFilesDialog
          open={sessionMediaOpen}
          onClose={() => {
            setSessionMediaOpen(false);
            reloadTableEvalVideos?.();
          }}
          tsId={tsId}
          grId={resolvedGrId}
          sessionDate={
            selectedSession?.ts_date ||
            selectedSession?.starts_at ||
            sessionMeta?.ts_date ||
            null
          }
          exercises={exercises}
          localeCode={(i18n.language || "en").slice(0, 2)}
          t={t}
        />
      ) : null}

      {canEditFinalizedTables ? (
        <MoveEvaluationVideoDialog
          open={Boolean(moveVideoTarget)}
          onClose={() => setMoveVideoTarget(null)}
          message={moveVideoTarget}
          tsId={tsId}
          currentTeId={teId}
          onMoved={handleEvalVideoMoved}
          onDeleted={handleEvalVideoMoved}
          zIndex={16000}
        />
      ) : null}

      <KotCoachResultDialog
        open={Boolean(kotSubmit.activeMatch)}
        match={kotSubmit.activeMatch}
        leaderboard={kotSnapshot?.leaderboard || []}
        coach={kotSubmit}
        formatPlayerLabel={(player, playerId) =>
          kotPlayerCell(player, playerId).name
        }
      />

      {selectedSession && selectedgrp ? (
        <PlayerPresenceDialog
          open={playerPresenceOpen}
          onClose={() => setPlayerPresenceOpen(false)}
          selectedSession={selectedSession}
          selectedgrp={selectedgrp}
          trainingTables={
            tableSetup.stationRawTables?.length
              ? tableSetup.stationRawTables
              : tableSetup.rawTables
          }
          trainingSelectedExercise={exercise}
          tsgId={stationTsgId}
          stations={stations}
          zIndex={1600}
          presentation="sheet"
          onAfterThirdPlayerTableAssign={handlePlayerPresenceUpdate}
          onUpdate={handlePlayerPresenceUpdate}
          onPlayerMarkedPresent={handlePlayerMarkedPresent}
          onDone={handlePresenceDone}
          presenceCorrectionOnly={isFinalized}
        />
      ) : null}

      <LiveTrainingCoachVoiceHold
        open={Boolean(voiceHoldTarget)}
        playerName={voiceHoldTarget?.name}
        receiverUserId={voiceHoldTarget?.userId}
        socket={socket}
        teId={teId}
        tsId={tsId}
        senderUserId={coachUserId}
        brandPrimary={brandPrimary}
        onFeedbackSent={onCoachTableFeedbackSent}
        onClose={() => setVoiceHoldTarget(null)}
      />

      <PersonalizeExerciseDialog
        open={personalizeOpen}
        onClose={() => {
          setPersonalizeOpen(false);
          setPersonalizePlayerId(null);
        }}
        container={livePortalRoot || undefined}
        exercise={exercise}
        groupPlayers={personalizeGroupPlayers}
        selectedSession={selectedSession}
        selectedgrp={selectedgrp}
        exerciseTypes={exerciseTypes}
        currentlng={i18n.language}
        t={t}
        onExerciseSelected={handlePersonalizedExerciseSelected}
        preSelectedPlayerId={personalizePlayerId}
      />

      <LiveTrainingRecordInstructionsDialog
        open={instructionsDialogOpen}
        onClose={() => {
          if (!instructionRecording.recording) {
            setInstructionsDialogOpen(false);
          }
        }}
        tables={coachTableRows}
        status={instructionRecording.status}
        error={instructionRecording.error}
        tableNumber={instructionRecording.tableNumber}
        onStart={(tn) => instructionRecording.start(tn)}
        onStop={() => instructionRecording.stop()}
        onResetError={() => instructionRecording.resetError()}
      />
    </div>
  );
}

export default LiveTrainingCoachDashboard;
